/**
 * InfoTooltip — Hover popover for field-level content guidance.
 *
 * Shows a small (i) icon next to field labels. On hover, reveals a
 * floating tooltip explaining what the text should achieve, not just
 * what the field is.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';

interface InfoTooltipProps {
  /** What the field should achieve — action-oriented guidance */
  tip: string;
  /** Optional maximum character count to display in the tooltip */
  charLimit?: string;
  /** Which side to prefer. Will flip if too close to edge. */
  side?: 'top' | 'bottom';
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({ tip, charLimit, side = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>(side);
  const iconRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible || !iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    // Flip if tooltip would overflow viewport
    if (side === 'top' && rect.top < 120) {
      setPosition('bottom');
    } else if (side === 'bottom' && window.innerHeight - rect.bottom < 120) {
      setPosition('top');
    } else {
      setPosition(side);
    }
  }, [visible, side]);

  return (
    <span
      ref={iconRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '4px', cursor: 'help' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <Info size={12} style={{ color: '#9ca3af', transition: 'color 0.15s' }} />

      {visible && (
        <div
          ref={tooltipRef}
          style={{
            position: 'absolute',
            zIndex: 60,
            [position === 'top' ? 'bottom' : 'top']: '22px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '240px',
            padding: '10px 12px',
            background: '#1f2937',
            color: '#f3f4f6',
            borderRadius: '8px',
            fontSize: '11px',
            lineHeight: 1.55,
            fontWeight: 400,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            pointerEvents: 'none',
          }}
        >
          {/* Arrow */}
          <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            ...(position === 'top'
              ? { bottom: '-5px', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1f2937' }
              : { top: '-5px', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '6px solid #1f2937' }
            ),
            width: 0, height: 0,
          }} />

          {tip}
          {charLimit && (
            <div style={{ marginTop: '6px', padding: '4px 6px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', fontSize: '10px', color: '#d1d5db' }}>
              {charLimit}
            </div>
          )}
        </div>
      )}
    </span>
  );
};

/**
 * Content guidance definitions for every social sharing field.
 * Each tip explains what the text should ACHIEVE, not just what the field is.
 */
export const SOCIAL_FIELD_TIPS = {
  socialTitle: {
    tip: 'Write a headline that makes someone stop scrolling. Different from your SEO title \u2014 social titles should trigger curiosity or promise a clear benefit. Lead with the most compelling word.',
    charLimit: 'Optimal: 40\u201360 chars. Facebook truncates at ~65, X at ~70.',
  },
  socialDescription: {
    tip: 'This is your pitch line. Give one reason someone should click through. Don\u2019t summarize the post \u2014 create a knowledge gap or promise a specific insight they\u2019ll get.',
    charLimit: 'Optimal: 100\u2013150 chars. X shows ~125, Facebook ~110.',
  },
  socialImage: {
    tip: 'This single image determines whether your post gets clicks. Use a clean, high-contrast visual with minimal text. Faces and action shots outperform illustrations. Must be 1200\u00d7630px for OG.',
    charLimit: 'Format: JPG or PNG. Max 5MB. Ratio: 1.91:1',
  },
  socialImageAlt: {
    tip: 'Describe the image for screen readers and when images fail to load. Be specific: "Coach Cedar weekly plan shown on iPhone" not "app screenshot". Also improves SEO image signals.',
    charLimit: 'Optimal: 50\u2013125 chars',
  },
  canonicalUrl: {
    tip: 'Only override this if the content was originally published elsewhere and you want search engines to credit the original URL. Leave blank for original Coach Cedar content \u2014 it auto-generates from the slug.',
  },
  xCardType: {
    tip: '"Large Image" fills the feed width \u2014 much higher engagement. "Summary" shows a small thumbnail on the left. Use Summary only for text-heavy/utility posts without a strong hero visual.',
  },
  xTitle: {
    tip: 'X readers scan fast. If your OG title works for X, leave this blank. Override only if you want a punchier, more conversational headline for the X audience. Emojis can work here if on-brand.',
    charLimit: 'Max: 70 chars. Shorter is better on X.',
  },
  xDescription: {
    tip: 'X descriptions appear below the title in the card. Keep it tight \u2014 one short sentence that adds context the title didn\u2019t cover. Leave blank to inherit from Social Description.',
    charLimit: 'Max: 125 chars',
  },
  xImage: {
    tip: 'Override the OG image for X only. Useful if your OG image has too much text that gets clipped on X\u2019s card format, or if you want to A/B test different visuals per platform.',
    charLimit: 'Ratio: 2:1 for large image, 1:1 for summary',
  },
  xImageAlt: {
    tip: 'Screen reader text for the X card image. Be descriptive and concise. Falls back to the main Image Alt Text if left blank.',
    charLimit: 'Max: 125 chars',
  },
  pinterestImage: {
    tip: 'Pinterest is a vertical platform \u2014 landscape images get buried. Upload a tall image (1000\u00d71500) with the post title overlaid as text. Pinterest pins with text on the image get 2\u20133\u00d7 more saves.',
    charLimit: 'Optimal: 1000\u00d71500px (2:3 ratio). JPG or PNG.',
  },
  seoTitle: {
    tip: 'This is the blue link in Google search results. Front-load your primary keyword. Different from the social title \u2014 this should match search intent, not feed-scrolling behavior.',
    charLimit: 'Optimal: 50\u201360 chars. Google truncates at ~60.',
  },
  seoDescription: {
    tip: 'The two-line blurb under the blue link in Google. Write it as a compelling reason to click, not a summary. Include the primary keyword naturally. This text also feeds LLM context windows.',
    charLimit: 'Optimal: 120\u2013155 chars. Google truncates at ~155.',
  },
  excerpt: {
    tip: 'Shown on blog list cards and used as a fallback for social descriptions and meta descriptions. Write it as a standalone hook that works in all three contexts.',
  },
} as const;
